import { debounce } from "throttle-debounce";

import { object } from "../helpers/object";
import { debug } from "../helpers/debug";
import { responsive } from "../helpers/responsive";
import { utils } from "../helpers/utils";

import { navigation } from "./navigation";
import { mask } from "../components/mask";
import { gtm } from "../components/gtm";
import { services } from "../helpers/service";
import { tpl } from "../helpers/tpl";
import { theme } from "../helpers/theme";
import { importLib } from "../helpers/async-libs";


/*------------------------------------*\
  #SEARCH

  Class gestion de la search principale
  du site. 

  [data-js-module="search"]
  [data-js-trigger="search--input"] // déclencheur de l'auto-completion de recherche à la saisi dans l'input du champ de recherche
  [data-js-trigger="search--mobile"] // déclencheur ouvrir|fermer le champ de recherche en mobile
  [data-js-search-mobile-open="{bool}"] // état d'affichage du champ de recherche en mobile
  [data-js-search-suggest-open="{bool}"] // état d'affichage des résultat du suggest en desktop
\*------------------------------------*/
class Search {
  DOM_triggers_input;
  STATE_mobileOpen;
  STATE_suggestOpen;
  searchValue;

  constructor(module){
    this.MODULE = module;
    this.DOM_MODULE = object.toArray(document.querySelectorAll(this.MODULE));
    this.searchValue = '';
    this.TIMEOUT_SUGGEST = null;
  }
  /**
   * INITIALISATION
   * @returns 
   */
  init = () => {
    if (!this.DOM_MODULE.length) return;
    // DOM ELEMENTS
    this.DOM_triggers_input = document.querySelector('[data-js-trigger="search--input"]');
    this.DOM_state_mobileOpen = document.querySelector('[data-js-search-mobile-open]');
    this.DOM_state_suggestOpen = document.querySelector('[data-js-search-suggest-open]');
    this.DOM_state_searchSuggest = document.querySelector('[data-js-search-suggest]');
    // DEFAULTS STATES
    this.STATE_mobileOpen = utils.dataBool(this.DOM_state_mobileOpen.getAttribute('data-js-search-mobile-open'));
    this.STATE_suggestOpen = utils.dataBool(this.DOM_state_suggestOpen.getAttribute('data-js-search-suggest-open'));
    // DEFAULT EVENT
    this.DOM_MODULE[0].addEventListener('submit', this.submitHandler)
    // CLICK EVENTS
    document.addEventListener('click', this.clickHandler);
    // KEY EVENTS
    document.addEventListener('keyup', this.keyHandler);
    // event search redirect
    this.internalSearchRedirect();

    if(debug.DEBUG) console.log('modules/search.js');
  }

  /**
   * CLICK HANDLER
   * 
   * Gestionnaire des event de la barre de navigation
   * @param {event} event 
   * @returns 
   */
  clickHandler = (event) => {
    if (event.target.matches('[data-js-trigger-prevent]')) return;
    if (event.target.matches('[data-js-trigger="search--mobile"]')) {
      // TOGGLE AFFICHAGE SEARCH MOBILE
      navigation.closeNav();
      this.libBarecodeScannerReset();
      this.mobileOpen = !this.mobileOpen;
      mask.display = this.mobileOpen ? "header" : false;
      this.focusInput();
    } else if (event.target.matches('[data-js-trigger="mask"]')) {
      // TOGGLE CLICK MASK
      this.closeAll();
    } else if (event.target.matches('[data-js-trigger="search--submit"]')) {
      // TOGGLE AFFICHAGE SUGGEST
      if (!responsive.is('lg')) {
        this.submitHandler(event);
      } else if (this.suggestOpen && this.searchSuggest !== 'onload') {
        this.DOM_triggers_input.value = '';
        this.closeAll();
      }
      event.preventDefault();
    } else if (event.target.closest('[data-js-module="search"]')) {
      // Handle event when the target's parent has the data-js-module="search"
      gtm.track({ 
        'event': 'internalSearch',
        'autocompletedKeyword': this.searchValue
      });
    } 
  }
  /**
   * CLOSE ON ESC KEY
   * 
   * Fermeture des sub-nav au clic sur la touche echap
   * @param {event} event 
   */
  keyHandler = (event) => {
    let key = event.key || event.keyCode;
    if (utils.isEscapKey(key)) {
      event.target.blur();
      this.suggestOpen = false;
      this.mobileOpen = false;
    } else if (utils.isEnterKey(key)) {
      this.submitHandler(event);

    } else if (event.target.matches('[data-js-trigger="search--input"]')) {
      this.suggest(event)
    }
  }
  /**
   * Focuses the input element if it exists.
   * 
   * This method checks if the `DOM_triggers_input` property is defined.
   * If it is, it sets the focus to the input element.
   * If it is not, the method returns without performing any action.
   */
  focusInput = () => {
    if (!this.DOM_triggers_input) return;
    this.DOM_triggers_input.focus();
  }
  /**
   * INTERNAL SEARCH REDIRECT
   * 
   * Checks if the body has the class 'internalSearchRedirect' and a valid 'data-ref' attribute.
   * If both conditions are met, it tracks the 'internalSearchRedirect' event with the 'autocompletedKeyword'.
   */
  internalSearchRedirect = () => {
    const body = document.body;
    const ref = body.getAttribute('data-ref');
    if (body.matches('[internalsearchredirect]') && ref) {
      gtm.track({
        event: 'internalSearchRedirect',
        autocompletedKeyword: ref
      });
    }
  }
  /**
   * Handles the form submission event.
   * 
   * @param {Event} event - The form submission event.
   * @returns {boolean} - Returns false to prevent default form submission on desktop.
   * 
   * @description
   * If the screen size is medium (md) or larger, it prevents the default form submission
   * and clears the input value if the suggestion box is open. Otherwise, it allows the 
   * form to be submitted, redirecting to the search results page.
   */
  submitHandler = (event) => {
    if (this.searchValue) {
      this.DOM_triggers_input.value = this.searchValue;
      this.DOM_MODULE[0].submit();
    }
  }
  libBarecodeScannerReset = () => {
    if (!responsive.is('md')) {
      importLib('barecode-scanner.js').then((barecodeScanner) => {
        barecodeScanner.reset();
      });
    }
  }
  /**
   * CLOSE ALL
   */
  closeAll = () => {
    this.libBarecodeScannerReset();
    this.suggestOpen = false;
    this.mobileOpen = false;
    mask.display = false;
  }

  /**
   * Debounced function to handle search suggestions.
   * 
   * @param {Event} event - The event object from the input field.
   * @returns {void}
   * 
   * This function stops the propagation and default behavior of the event,
   * retrieves the search value from the input field, and checks if the 
   * screen size is large, the search value length is greater than 1, and 
   * the key pressed is not the escape key. If these conditions are met, 
   * it closes all navigation, sets the suggestOpen flag to true, and 
   * displays the mask. Otherwise, it sets the suggestOpen flag to false.
   */
  suggest = debounce(100, (event) => {
    // event.stopPropagation();
    // event.preventDefault();
    let key = event.key || event.keyCode;
    this.searchValue = event.target.value;
    if( responsive.is('lg') && this.searchValue.length > 1 && !utils.isEscapKey(key)) {
      if (responsive.is('xl')) navigation.closeAll();
      this.suggestOpen = true;
      this.searchSuggest = 'onload';
      mask.display = "header";
      
      clearTimeout(this.TIMEOUT_SUGGEST);
      this.TIMEOUT_SUGGEST = setTimeout(() => {
        services.use('suggest', { s: this.searchValue, fail:false })
          .then((response) => {
            
            this.generateSuggestResults(response.datas.suggestPro.elements, 'products-pro');
            this.generateSuggestResults(response.datas.suggestPublic.elements, 'products-part');
            this.generateSuggestResults(response.datas.suggestFaq.elements, 'faq');
            this.generateSuggestResults(response.datas.suggestDoc.elements, 'docs');

            this.searchSuggest = 'complete';
          }).catch((error) => {
            console.error(error);
            this.searchSuggest = 'error';
          });

      }, 1000);
    } else {
      this.suggestOpen = false;
    }
  });

  tplTag = (className, label) => {
    return `<span class="c-tag ${className}">${label}</span>`;
  }
  /**
   * Generates and inserts a product template into the specified container.
   *
   * @param {Object} item - The product item to be displayed.
   * @param {string} item.label - The label of the product.
   * @param {string} item.media - The media URL of the product.
   * @param {string} item.href - The hyperlink reference of the product.
   * @param {string} item.ref - The reference of the product.
   * @param {string} item.range - The range of the product.
   * @param {boolean} item.remplacement - Indicates if the product is a replacement.
   * @param {HTMLElement} container - The container where the product template will be inserted.
   * @param {string} type - The type of the search results (e.g., 'products-pro', 'products-part').
   */
  tplProduct = (container, item, type) => {
    if (!container) return;
    const caption = type === 'products-pro' ? `réf. ${item.ref}` : item.range;
    
    let tags = '';
    tags += item.new ? this.tplTag(type === 'products-pro' ? 'c-tag--pro' : 'c-tag--part', 'Nouveauté') : '';
    tags += item.delete ? this.tplTag('c-tag--delete', 'Supprimé') : '';
    tags += item.remplacement ? this.tplTag('c-tag--remplacement', 'Produit de remplacement') : '';
    
    const itemDOM = tpl.get({
      title: this.emphasize(item.label, this.searchValue),
      image: item.media,
      link: item.href,
      caption: caption,
      tags: tags,
    }, 'tpl-card-search');
    
    container.insertAdjacentHTML('beforeend', itemDOM);
  
    const elementTag = container.lastElementChild.querySelector('.c-card__tags');
    if (tags === '') {
      elementTag.remove();
    }
  };
  
  /**
   * Inserts a document card into the specified container.
   *
   * @param {HTMLElement} container - The container element where the document card will be inserted.
   * @param {Object} item - The item object containing details for the document card.
   * @param {string} item.label - The label for the document card.
   * @param {string} item.href - The URL for the document card.
   * @param {string} [item.image='/sites/all/themes/legrand/img/data/Logo-Produit.png'] - The image URL for the document card. Defaults to a placeholder image if not provided.
   */
  tplDocs = (container, item) => {
    if (!container) return;
    let itemDOM = tpl.get({
      label: this.emphasize(item.label, this.searchValue),
      href: item.href,
      image: item.image ?? '/sites/all/themes/legrand/img/data/Logo-Produit.png',
    }, 'tpl-card-docs');
    container.insertAdjacentHTML('beforeend', itemDOM);
  }
  /**
   * Inserts a FAQ item into the specified container.
   *
   * @param {HTMLElement} container - The container element where the FAQ item will be inserted.
   * @param {Object} item - The FAQ item to be inserted.
   * @param {string} item.label - The label of the FAQ item.
   * @param {string} item.href - The href of the FAQ item.
   */
  tplFaq = (container, item) => {
    if (!container) return;
    let itemDOM = tpl.get({
      label: this.emphasize(item.label, this.searchValue),
      href: item.href,
    }, 'tpl-item-faq');
    container.insertAdjacentHTML('beforeend', itemDOM);
  }
  /**
   * Generates and inserts a link template into the specified container.
   *
   * @param {HTMLElement} link - The link where the link template will be inserted.
   * @param {string} type - The type of the search results (e.g., 'products-pro', 'products-part').
   */
  tplLink = (link, type) => {
    if (!link) return;

    link.classList.remove('u-hidden');
    let url = link.getAttribute('href');
    let updatedUrl = url.replace(/(\?|&)s=[^&]*/, `$1s=${this.searchValue}`);
    if (!url.includes('?s=')) {
      updatedUrl = `${url}${url.includes('?') ? '&' : '?'}s=${this.searchValue}`;
    }
    if (type === 'faq' || type === 'docs') {
      if (theme.isPro()) {
        if (!url.startsWith('/pro')) {
        updatedUrl = '/pro' + updatedUrl;
        }
      } else {
        updatedUrl = updatedUrl.replace(/^\/pro/, '');
      }
    }
    link.setAttribute('href', updatedUrl);
  }
  /**
   * Generates suggestion results and updates the DOM based on the provided items and type.
   *
   * @param {Array} items - The array of items to display as suggestions.
   * @param {string} type - The type of suggestions to generate (e.g., 'products-pro', 'products-part', 'faq', 'docs').
   *
   * @returns {void}
   */
  generateSuggestResults = (items, type) => {
    let container = document.querySelector(`[data-js-suggest-results="${type}"]`);
    let link = document.querySelector(`[data-js-suggest-link="${type}"]`);
    if (!container) return;
    container.innerHTML = '';

    if (items.length === 0) { 
      container.insertAdjacentHTML('beforeend', tpl.get({}, 'tpl-no-results'));
      link.classList.add('u-hidden');
      return;
    }
    items.forEach(item => {
      // si le type est produits pro
      if (type === 'products-pro' || type === 'products-part') { 
        this.tplProduct(container, item, type);
        this.tplLink(link, type);
      }
      else if (type === 'faq') {
        this.tplFaq(container, item);
        this.tplLink(link, type);
      }
      else if (type === 'docs') {
        this.tplDocs(container, item);
        this.tplLink(link, type);
      }
    });
  };
  emphasize = (suggest, keyword) => {
    if (!keyword || !suggest) return suggest;
    return suggest.replace(new RegExp(`(${keyword})`, 'gi'), '<strong>$1</strong>');
  };
  
  /**
   * Sets the state of the search suggest attribute.
   * 
   * @param {boolean} show - A boolean indicating whether to show or hide the search suggest attribute.
   */
  set searchSuggest (show) {
    if (!this.DOM_state_searchSuggest) return;
    this.DOM_state_searchSuggest.setAttribute('data-js-search-suggest', show);
  }

  /**
   * Gets the state of the search suggest attribute.
   * 
   * @returns {boolean} - The current state of the search suggest attribute.
   */
  get searchSuggest () {
    if (!this.DOM_state_searchSuggest) return false;
    return this.DOM_state_searchSuggest.getAttribute('data-js-search-suggest');
  }
  /**
   * Sets the mobile open state.
   * 
   * @param {boolean} show - The state to set for mobile open.
   */
  set mobileOpen (show) {
    if (!this.DOM_state_mobileOpen) return;
    this.STATE_mobileOpen = show;
    this.DOM_state_mobileOpen.setAttribute('data-js-search-mobile-open', show);
  }

  get mobileOpen () {
    return this.STATE_mobileOpen;
  }

  /**
   * Sets the state of the search suggestion dropdown.
   * 
   * @param {boolean} show - A boolean indicating whether to show or hide the search suggestion dropdown.
   */
  set suggestOpen (show) {
    if (!this.DOM_state_suggestOpen) return;
    this.STATE_suggestOpen = show;
    this.DOM_state_suggestOpen.setAttribute('data-js-search-suggest-open', show);
  }

  get suggestOpen () {
    return this.STATE_suggestOpen;
  }
}
export const search = new Search('[data-js-module="search"]'); 